import { useAuth0Context } from "@/context/Auth0Context";
import { hasAnyRole } from "@/lib/helpers/permissionHelpers";
import { NAVIGATION, PROTECTED_ROUTES } from "@/router/router";
import { ROLES } from "@/types/permissions";
import type { NavigationItem, RouteConfig } from "@/types/router";
import { useMemo } from "react";

export const useRoleBasedNavigation = () => {
  const { roles, isAuthenticated, primaryRole } = useAuth0Context();

  const availableRoutes = useMemo((): RouteConfig[] => {
    if (!isAuthenticated) return [];

    return PROTECTED_ROUTES.filter((route) => {
      if (route.roles.length === 0) return true; // Public routes
      return hasAnyRole(roles, route.roles);
    });
  }, [isAuthenticated, roles]);

  const navigationItems = useMemo((): NavigationItem[] => {
    if (!isAuthenticated) return [];

    const filterNavItems = (items: NavigationItem[]): NavigationItem[] => {
      return items
        .filter((item) => {
          const hasItemRole = hasAnyRole(roles, item.roles);
          if (item.id === "about") return true;
          if (!hasItemRole) return false;

          return true;
        })
        .map((item) => ({
          ...item,
          children: item.children ? filterNavItems(item.children) : undefined,
        }));
    };

    return filterNavItems(NAVIGATION);
  }, [isAuthenticated, roles]);

  const userHeaderNavigationItems = useMemo((): NavigationItem[] => {
    if (!isAuthenticated) return [];
    return navigationItems;
  }, [isAuthenticated, navigationItems]);

  // Get the appropriate dashboard URL based on user roles
  const getDefaultDashboard = useMemo((): string => {
    if (!isAuthenticated || !roles.length) {
      return "/login";
    }

    // Check for internal staff roles (highest priority)
    if (
      hasAnyRole(roles, [
        ROLES.INTERNAL_ADMIN,
        ROLES.CONVERSATION_REVIEWER,
        ROLES.USER_ADMIN,
      ])
    ) {
      return "/simulator";
    }

    // Check for client roles
    if (hasAnyRole(roles, [ROLES.CLIENT_TRAINEE, ROLES.CLIENT_MANAGER])) {
      return "/simulator";
    }

    // Fallback
    return "/login";
  }, [isAuthenticated, roles]);

  return {
    availableRoutes,
    navigationItems,
    userHeaderNavigationItems,
    getDefaultDashboard,
  };
};
